'use client'

import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useTranslation } from 'react-i18next'

import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Switch } from '@/components/ui/switch'

// ============================================================================
// Types & Schema
// ============================================================================

const taxjarFormSchema = z.object({
	apiToken: z.string().min(1, 'API Token is required'),
	sandboxed: z.boolean(),
	enableReporting: z.boolean(),
})

type FormValues = z.infer<typeof taxjarFormSchema>

interface TaxjarFormProps {
	defaultValues?: Record<string, any>
	onSubmit: (data: Record<string, any>) => Promise<void>
	isLoading?: boolean
}

// ============================================================================
// Component
// ============================================================================

export function TaxjarForm({
	defaultValues = {},
	onSubmit,
	isLoading = false,
}: TaxjarFormProps) {
	const { t } = useTranslation('integrations')

	const form = useForm<FormValues>({
		resolver: zodResolver(taxjarFormSchema),
		defaultValues: {
			apiToken: defaultValues.credentials?.apiToken ?? '',
			sandboxed: defaultValues.credentials?.sandboxed ?? false,
			enableReporting: defaultValues.credentials?.enableReporting ?? false,
		},
	})

	const { register, handleSubmit, formState: { errors }, watch, setValue } = form

	const sandboxed = watch('sandboxed')
	const enableReporting = watch('enableReporting')

	const handleFormSubmit = async (data: FormValues) => {
		const credentials = data
		await onSubmit({ credentials })
	}

	return (
		<form id="integration-config-form" onSubmit={handleSubmit(handleFormSubmit)} className="space-y-6">
			<Card>
				<CardHeader>
					<CardTitle>{t('taxjar.credentialsTitle')}</CardTitle>
					<CardDescription>{t('taxjar.credentialsDescription')}</CardDescription>
				</CardHeader>
				<CardContent className="space-y-4">
					<div className="space-y-2">
						<Label htmlFor="apiToken">{t('taxjar.apiTokenLabel')}</Label>
						<Input
							id="apiToken"
							type="password"
							placeholder={t('taxjar.apiTokenPlaceholder')}
							disabled={isLoading}
							{...register('apiToken')}
						/>
						{errors.apiToken && (
							<p className="text-xs text-destructive">{t('taxjar.apiTokenRequired')}</p>
						)}
					</div>

					<div className="flex items-center justify-between rounded-lg border p-4">
						<div className="space-y-0.5">
							<Label htmlFor="sandboxed">{t('taxjar.sandboxLabel')}</Label>
							<p className="text-xs text-muted-foreground">{t('taxjar.sandboxHint')}</p>
						</div>
						<Switch
							id="sandboxed"
							checked={sandboxed}
							onCheckedChange={(checked) => setValue('sandboxed', checked)}
							disabled={isLoading}
						/>
					</div>

					<div className="flex items-center justify-between rounded-lg border p-4">
						<div className="space-y-0.5">
							<Label htmlFor="enableReporting">{t('taxjar.enableReportingLabel')}</Label>
							<p className="text-xs text-muted-foreground">{t('taxjar.enableReportingHint')}</p>
						</div>
						<Switch
							id="enableReporting"
							checked={enableReporting}
							onCheckedChange={(checked) => setValue('enableReporting', checked)}
							disabled={isLoading}
						/>
					</div>
				</CardContent>
			</Card>

			<Card>
				<CardHeader>
					<CardTitle>{t('taxjar.setupInstructions')}</CardTitle>
				</CardHeader>
				<CardContent>
					<div className="rounded-lg bg-muted p-4">
						<ol className="list-inside list-decimal space-y-3 text-sm text-muted-foreground">
							<li>
								{t('taxjar.setupStep1')}{' '}
								<a
									href="https://app.taxjar.com"
									target="_blank"
									rel="noopener noreferrer"
									className="text-primary underline"
								>
									{t('taxjar.setupStep1Link')}
								</a>.
							</li>
							<li dangerouslySetInnerHTML={{ __html: t('taxjar.setupStep2') }} />
							<li>{t('taxjar.setupStep3')}</li>
							<li>{t('taxjar.setupStep4')}</li>
						</ol>
					</div>
				</CardContent>
			</Card>
		</form>
	)
}
